import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { fmtCurrency, fmtCurrencyK } from "../lib/formatters";
import { DelayedTooltipContent } from "../lib/DelayedTooltip";

// Cumulative Reserve Expenses — Illustrations-tab panel (moved off the
// Dashboard More menu). Stacked areas = money out; navy line = money in.
export default function CumulativeExpensesChart({ calc, variables, horizon }) {
  // Build the cumulative dataset. Runs unconditionally to keep hook order
  // stable across renders (open=false is handled below with early return).
  const { data, totalSA } = useMemo(() => {
    const rows = (calc?.projection || []).slice(0, horizon);
    const opening = variables?.opening_balance || 0;
    let cumExp = 0;
    let cumSA = 0;
    let cumContrib = opening; // starting point per Dave's spec
    const points = rows.map((r) => {
      cumExp += r.expenditures || 0;
      cumSA += r.special_assessment || 0;
      cumContrib += r.contribution || 0;
      return {
        year: r.year,
        expenditures: Math.round(cumExp),
        specialAssessments: Math.round(cumSA),
        cumContributions: Math.round(cumContrib),
        total: Math.round(cumExp + cumSA),
      };
    });
    return {
      data: points,
      totalExp: cumExp,
      totalSA: cumSA,
      totalContrib: cumContrib,
    };
  }, [calc, variables, horizon]);

  if (!calc) return null;

  return (
    <div className="panel-compact flex flex-col" style={{ height: "100%", minHeight: 0 }} data-testid="cumulative-expenses-panel">
      <div className="dash-panel-header">
        <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">Cumulative Expense</div>
        <div className="text-[10.5px] text-navy font-semibold font-mono tabular-nums" data-testid="cumulative-expenses-horizon-label">{horizon} Year Horizon</div>
      </div>
        {/* Chart body */}
        <div style={{ flex: 1, minHeight: 0, padding: "4px 0 0" }}>
          <ResponsiveContainer>
            <ComposedChart
              data={data}
              margin={{ top: 6, right: 16, bottom: 0, left: 0 }}
              stackOffset="none"
            >
              <defs>
                <linearGradient id="cumExpGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#88BFDE" stopOpacity={0.85} />
                  <stop offset="100%" stopColor="#88BFDE" stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="cumSAGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EA6A47" stopOpacity={0.85} />
                  <stop offset="100%" stopColor="#EA6A47" stopOpacity={0.55} />
                </linearGradient>
              </defs>

              <CartesianGrid stroke="#E8ECF1" vertical={false} />

              <XAxis
                dataKey="year"
                tick={{ fill: "#1A1A1A", fontSize: 9, fontFamily: "IBM Plex Mono" }}
                axisLine={{ stroke: "#1A1A1A" }}
                tickLine={{ stroke: "#1A1A1A" }}
                interval="preserveStartEnd"
                minTickGap={8}
                height={18}
              />
              <YAxis
                tick={{ fill: "#1A1A1A", fontSize: 9, fontFamily: "IBM Plex Mono" }}
                axisLine={{ stroke: "#1A1A1A" }}
                tickLine={{ stroke: "#1A1A1A" }}
                tickFormatter={fmtCurrencyK}
                width={50}
                domain={[0, "auto"]}
              />

              <Tooltip
                content={
                  <DelayedTooltipContent
                    formatter={(v, name) => [fmtCurrency(v), name]}
                    labelFormatter={(v) => `Year ${v}`}
                    labelStyle={{ fontWeight: 700, color: "#1C4E80" }}
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #1C4E80",
                      borderRadius: 3,
                      fontSize: 11,
                      textAlign: "right",
                    }}
                  />
                }
                wrapperStyle={{ pointerEvents: "none" }}
              />

              <Area
                type="monotone"
                dataKey="expenditures"
                name="Reserve Expenditures"
                stackId="1"
                stroke="#1C4E80"
                strokeWidth={1.5}
                fill="url(#cumExpGrad)"
                isAnimationActive={false}
                dot={{ r: 1.75, fill: "#88BFDE", stroke: "#1C4E80", strokeWidth: 1 }}
                activeDot={{ r: 3, fill: "#1C4E80", stroke: "#fff", strokeWidth: 2 }}
              />
              {totalSA > 0 && (
                <Area
                  type="monotone"
                  dataKey="specialAssessments"
                  name="Special Assessments"
                  stackId="1"
                  stroke="#C24B2C"
                  strokeWidth={1.5}
                  fill="url(#cumSAGrad)"
                  isAnimationActive={false}
                  dot={{ r: 1.75, fill: "#F5B199", stroke: "#C24B2C", strokeWidth: 1 }}
                  activeDot={{ r: 3, fill: "#C24B2C", stroke: "#fff", strokeWidth: 2 }}
                />
              )}

              {/* Cumulative Contributions — dark navy line, NOT stacked.
                  Starts at variables.opening_balance and adds annual
                  contribution for each year. Rendered LAST so it draws
                  above the stacked areas. */}
              <Line
                type="monotone"
                dataKey="cumContributions"
                name="Cumulative Contributions"
                stroke="#0F2A44"
                strokeWidth={1.25}
                fill="none"
                isAnimationActive={false}
                dot={{ r: 1.75, fill: "#88BFDE", stroke: "#0F2A44", strokeWidth: 1 }}
                activeDot={{ r: 3, fill: "#0F2A44", stroke: "#fff", strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div
          className="flex items-center justify-center gap-4 pt-1 pb-0.5 text-[9.5px] flex-wrap"
          data-testid="cumulative-expenses-legend"
        >
          <span className="inline-flex items-center gap-1.5 text-ink">
            <span
              style={{
                width: 11,
                height: 8,
                background: "#88BFDE",
                display: "inline-block",
              }}
            />
            Reserve Expenditures
          </span>
          <span className="inline-flex items-center gap-1.5 text-ink">
            <span
              style={{
                width: 11,
                height: 8,
                background: "#EA6A47",
                display: "inline-block",
              }}
            />
            Special Assessments
          </span>
          {/* Line + dot swatch for the new contributions series */}
          <span className="inline-flex items-center gap-1.5 text-ink">
            <span
              style={{
                width: 18,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 2,
                  background: "#1C4E80",
                  display: "inline-block",
                  borderRadius: 2,
                }}
              />
              <span
                style={{
                  position: "absolute",
                  width: 7,
                  height: 7,
                  background: "#88BFDE",
                  border: "1.5px solid #1C4E80",
                  borderRadius: "50%",
                  boxSizing: "border-box",
                }}
              />
            </span>
            Cumulative Contributions
          </span>
        </div>

    </div>
  );
}
